import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React from "react";
import {
  EntitySelector,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  Icon,
  Text,
  useCloseMenu,
  type EntitySelectorConfig,
  type EntitySelection,
} from "@salestrak/ui";

import {
  DROPDOWN_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import { IssueTypePool } from "#app/components/entity-selectors/issue-type-pool";
import { MoveWorkflowRows } from "#app/components/issues/move-workflow-picker";
import {
  STAGE_POOL_ID,
  USER_POOL_ID,
  useIssueActions,
  type IssueActionRef,
} from "#app/hooks/kanban/use-issue-actions";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";


type IssueMenuItemsProps = Omittable<{
  /** Menu part set of the hosting menu kind. Defaults to the dropdown parts. */
  parts?: MenuParts;
  /** Host-specific rows seated with the opening/navigation actions. */
  prepend?: React.ReactNode;
  isFavorited: boolean;
  isArchived: boolean;
  hasStages: boolean;
  hasOtherWorkflows: boolean;
  hasIssueTypes: boolean;
  stageConfig: EntitySelectorConfig;
  stageValue: EntitySelection[];
  /** The project's current workflow — the move picker excludes it. */
  workflowId: string;
  issueTypeConfig: EntitySelectorConfig;
  issueTypeValue: EntitySelection[];
  assigneeConfig: EntitySelectorConfig;
  assigneeSelections: EntitySelection[];
  handleStageValueChange: (selections: EntitySelection[]) => void;
  handleAssigneeValueChange: (selections: EntitySelection[]) => void;
  handleMoveWorkflow: (workflowId: string, stageId: string) => void;
  handleIssueTypeValueChange: (selections: EntitySelection[]) => void;
  handleFavorite: () => void;
  handleCopyLink: () => void;
  handleOpenInNewTab: () => void;
  handleArchive: () => void;
  // Opens the "Automation activity" dialog (every automation run that fired on
  // this issue). Sits under the top group. Omitted → the row is hidden — the
  // host (IssueMenu) only passes it when automations are enabled + permitted,
  // so other menu consumers (favorites, table) don't show it.
  onAutomationRuns?: () => void;
  onRename: () => void;
  onDelete: () => void;
  // Opens the item Share dialog. Omit (or pass canManageSharing=false)
  // to hide the row — gating mirrors the workflow share action (only
  // viewers with `full_edit` on the item can manage its share list).
  onShare?: () => void;
  canManageSharing?: boolean;
  // Role-permission gates for the destructive footer. These OVERLAY
  // readOnly: a Guest with `delete.items` granted should still see
  // Delete even when their object access is view/comment, because the
  // backend allows the action.
  canDelete?: boolean;
  canArchive?: boolean;
  // When true, only the non-mutating items render, plus any destructive action
  // that `canDelete` /
  // `canArchive` explicitly enables.
  readOnly?: boolean;
  /** Closes the hosting menu. A pick of one stage, workflow or type ends the
   *  task; the assignee picker takes several, so it stays open. */
  onPicked?: () => void;
}>;
export function renderIssueMenuItems({
  parts = DROPDOWN_MENU_PARTS,
  prepend,
  isFavorited,
  isArchived,
  hasStages,
  hasOtherWorkflows,
  hasIssueTypes,
  stageConfig,
  stageValue,
  workflowId,
  issueTypeConfig,
  issueTypeValue,
  assigneeConfig,
  assigneeSelections,
  handleStageValueChange,
  handleAssigneeValueChange,
  handleMoveWorkflow,
  handleIssueTypeValueChange,
  handleFavorite,
  handleCopyLink,
  handleOpenInNewTab,
  handleArchive,
  onAutomationRuns,
  onRename,
  onDelete,
  onShare,
  canManageSharing = false,
  canDelete = true,
  canArchive = true,
  readOnly = false,
  onPicked,
}: IssueMenuItemsProps) {
  const thenClose =
    (pick: (selections: EntitySelection[]) => void) => (selections: EntitySelection[]) => {
      pick(selections);
      onPicked?.();
    };

  const {
    Item,
    Separator,
    Sub,
    SubTrigger,
    SubPopup,
  } = parts;

  const openInNewTabItem = (
    <Item onClick={handleOpenInNewTab}>
      <Icon name="external-link" size="md" color="muted" />
      <Text size="sm">Open in new tab</Text>
    </Item>
  );

  // One "Copy" door for the clipboard verbs — link today, ID once issues
  // carry a short readable one (that row stays disabled until then).
  const copyItems = (
    <>
      <Sub>
        <SubTrigger>
          <Icon name="clipboard" size="md" color="muted" />
          <Text size="sm">Copy</Text>
        </SubTrigger>
        <SubPopup>
          <Item onClick={handleCopyLink}>
            <Icon name="link" size="md" color="muted" />
            <Text size="sm">Copy link</Text>
          </Item>
          <Item disabled disabledReason="Project IDs are coming soon">
            <Icon name="hash" size="md" color="muted" />
            <Text size="sm">Copy ID</Text>
          </Item>
        </SubPopup>
      </Sub>
    </>
  );

  const favoriteItem = (
    <Item onClick={handleFavorite}>
      <Icon name={isFavorited ? "star-filled" : "star"} size="md" color="muted" />
      <Text size="sm">{isFavorited ? "Unfavorite" : "Favorite"}</Text>
    </Item>
  );

  // The "Automation activity" row, rendered under the top group in both the
  // read-only and full menus. Only present when the host wired the handler.
  const automationRunsItem = onAutomationRuns ? (
    <Item onClick={onAutomationRuns}>
      <Icon name="activity" size="md" color="muted" />
      <Text size="sm">Automation activity</Text>
    </Item>
  ) : null;

  if (readOnly) {
    const showArchive = canArchive;
    const showDelete = canDelete;
    return (
      <>
        {prepend}
        {favoriteItem}
        <Separator />
        {openInNewTabItem}
        {copyItems}
        {automationRunsItem ? (
          <>
            <Separator />
            {automationRunsItem}
          </>
        ) : null}
        {showArchive || showDelete ? <Separator /> : null}
        {showArchive ? (
          <Item onClick={handleArchive}>
            <Icon
              name={isArchived ? "archive-restore" : "archive"}
              size="md"
              color="muted"
            />
            <Text size="sm">{isArchived ? "Unarchive" : "Archive"}</Text>
          </Item>
        ) : null}
        {showDelete ? (
          <Item variant="destructive" onClick={onDelete}>
            <Icon name="trash" size="md" />
            <Text size="sm">Delete</Text>
          </Item>
        ) : null}
      </>
    );
  }
  return (
    <>
      {prepend}

      {favoriteItem}

      <Item onClick={onRename}>
        <Icon name="pencil" size="md" color="muted" />
        <Text size="sm">Rename</Text>
      </Item>

      <Separator />

      {openInNewTabItem}

      {copyItems}

      <Separator />

      {automationRunsItem ? (
        <>
          {automationRunsItem}
          <Separator />
        </>
      ) : null}

      {hasStages && (
        <Sub>
          <SubTrigger>
            <Icon name={PROPERTY_ICON.stage} size="md" color="muted" />
            <Text size="sm">Stage</Text>
          </SubTrigger>
          <SubPopup padding="none" sheetTitle="Stage" width="panel">
            <EntitySelector
              variant="inline"
              config={stageConfig}
              value={stageValue}
              onValueChange={thenClose(handleStageValueChange)}
            >
              <EntitySelectorInput />
              <EntitySelectorPool value={STAGE_POOL_ID} />
              <EntitySelectorEmpty>No matching stages</EntitySelectorEmpty>
            </EntitySelector>
          </SubPopup>
        </Sub>
      )}

      <Sub>
        <SubTrigger>
          <Icon name={ASSIGNEE_ICON} size="md" color="muted" />
          <Text size="sm">Assignee</Text>
        </SubTrigger>
        <SubPopup padding="none" sheetTitle="Assignee" width="panel">
          <EntitySelector
            variant="inline"
            config={assigneeConfig}
            value={assigneeSelections}
            onValueChange={handleAssigneeValueChange}
          >
            <EntitySelectorInput />
            <EntitySelectorSelected
              label="Assignees"
              hideWhenEmpty
              hideBadge
              hideSelectionHighlight
            />
            <EntitySelectorPool value={USER_POOL_ID} hideSelectionHighlight />
            <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
          </EntitySelector>
        </SubPopup>
      </Sub>

      <Separator />

      <Sub>
        <SubTrigger disabled={!hasOtherWorkflows}>
          <Icon name={PROPERTY_ICON.workflow} size="md" color="muted" />
          <Text size="sm">Move to Workflow...</Text>
        </SubTrigger>
        <SubPopup width="panel">
          <MoveWorkflowRows
            parts={parts}
            workflowId={workflowId}
            onMove={(target, stageId) => {
              handleMoveWorkflow(target, stageId);
              onPicked?.();
            }}
          />
        </SubPopup>
      </Sub>

      {hasIssueTypes && (
        <Sub>
          <SubTrigger>
            <Icon name={PROPERTY_ICON.issueType} size="md" color="muted" />
            <Text size="sm">Project Type</Text>
          </SubTrigger>
          <SubPopup padding="none" sheetTitle="Project Type" width="panel">
            <EntitySelector
              variant="inline"
              config={issueTypeConfig}
              value={issueTypeValue}
              onValueChange={thenClose(handleIssueTypeValueChange)}
            >
              <EntitySelectorInput />
              <IssueTypePool />
              <EntitySelectorEmpty>No Project types</EntitySelectorEmpty>
            </EntitySelector>
          </SubPopup>
        </Sub>
      )}

      {canManageSharing && onShare ? (
        <>
          <Separator />
          <Item onClick={onShare}>
            <Icon name="users" size="md" color="muted" />
            <Text size="sm">Sharing &amp; Permissions</Text>
          </Item>
        </>
      ) : null}

      {(canArchive || canDelete) && <Separator />}

      {canArchive ? (
        <Item onClick={handleArchive}>
          <Icon
            name={isArchived ? "archive-restore" : "archive"}
            size="md"
            color="muted"
          />
          <Text size="sm">{isArchived ? "Unarchive" : "Archive"}</Text>
        </Item>
      ) : null}

      {canDelete ? (
        <Item variant="destructive" onClick={onDelete}>
          <Icon name="trash" size="md" />
          <Text size="sm">Delete</Text>
        </Item>
      ) : null}
    </>
  );
}

export function IssueMenuItemsWithActions({
  issue,
  parts,
  prepend,
  onRename,
  onDelete,
  onShare,
  canManageSharing,
  canDelete,
  canArchive,
  readOnly,
}: Omittable<{
  issue: IssueActionRef;
  parts?: MenuParts;
  prepend?: React.ReactNode;
  onRename: () => void;
  onDelete: () => void;
  onShare?: () => void;
  canManageSharing?: boolean;
  canDelete?: boolean;
  canArchive?: boolean;
  readOnly?: boolean;
}>) {
  const actions = useIssueActions(issue);
  const closeMenu = useCloseMenu();
  return renderIssueMenuItems({
    parts,
    prepend,
    ...actions,
    onPicked: closeMenu,
    onRename,
    onDelete,
    onShare,
    canManageSharing,
    canDelete,
    canArchive,
    readOnly,
  });
}
